import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Gift, Zap, Check, Loader2 } from "lucide-react";

const digits = (p) => (p || "").replace(/\D/g, "");

export const BookingRewards = ({ email, phone, usePoints, setUsePoints, referralCode, setReferralCode }) => {
  const [points, setPoints] = useState(null);
  const [ref, setRef] = useState(null);
  const [checkingRef, setCheckingRef] = useState(false);

  useEffect(() => {
    if (!email.includes("@") || digits(phone).length < 7) { setPoints(null); return; }
    const t = setTimeout(() => {
      api.post("/loyalty/check", { email, phone }).then((r) => setPoints(r.data)).catch(() => setPoints(null));
    }, 600);
    return () => clearTimeout(t);
  }, [email, phone]);

  useEffect(() => {
    if (points && !points.points_to_spend && usePoints) setUsePoints(false);
  }, [points, usePoints, setUsePoints]);

  useEffect(() => {
    const code = referralCode.trim();
    if (code.length < 8) { setRef(null); return; }
    setCheckingRef(true);
    const t = setTimeout(() => {
      api.get("/referral/validate", { params: { code } }).then((r) => setRef(r.data)).catch(() => setRef(null)).finally(() => setCheckingRef(false));
    }, 500);
    return () => clearTimeout(t);
  }, [referralCode]);

  const canRedeem = points?.points_to_spend > 0;

  return (
    <section className="bg-white p-6 sm:p-8" data-testid="booking-rewards">
      <p className="label-mono text-spark-teal mb-1">07 Rewards</p>
      <h2 className="font-display uppercase tracking-tight text-2xl font-bold mb-2">Spark Loyalty</h2>
      <p className="text-sm text-spark-slate mb-6">Discounts apply to labour only, up to a maximum of 10%.</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className={`rounded-md border p-4 ${canRedeem ? "border-spark-yellow bg-spark-yellow/10" : "border-spark-line"}`} data-testid="booking-points-box">
          <p className="label-mono text-spark-teal mb-2 flex items-center gap-1.5"><Zap className="size-3.5" /> Loyalty points</p>
          {points === null ? (
            <p className="text-xs text-spark-slate">Enter your email and phone above to see your points.</p>
          ) : canRedeem ? (
            <label className="flex items-start gap-3 cursor-pointer text-sm" data-testid="use-points-label">
              <input type="checkbox" checked={usePoints} onChange={(e) => setUsePoints(e.target.checked)} className="mt-0.5 size-4 accent-spark-teal shrink-0" data-testid="use-points-checkbox" />
              <span>
                Use <strong>{points.points_to_spend}</strong> of my <strong>{points.points}</strong> points for{" "}
                <strong className="text-spark-teal-dark">{points.discount_pct}% off labour</strong>
              </span>
            </label>
          ) : (
            <p className="text-xs text-spark-slate" data-testid="points-balance-low">
              You have <strong className="text-spark-ink">{points.points}</strong> points. You need 100 to unlock 2.5% off labour. You earn 1 point for every $1 spent on repairs.
            </p>
          )}
        </div>
        <div className="rounded-md border border-spark-line p-4">
          <label htmlFor="referral_code" className="label-mono text-spark-teal mb-2 flex items-center gap-1.5"><Gift className="size-3.5" /> Referred by a mate? (first booking only)</label>
          <input
            id="referral_code"
            className="input-style uppercase"
            placeholder="e.g. SPARK-JAMES27"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            data-testid="input-referral-code"
          />
          <div className="mt-2 text-xs min-h-[1rem]" data-testid="referral-status">
            {checkingRef ? <Loader2 className="size-3.5 animate-spin text-spark-teal" />
              : ref?.valid ? <span className="text-emerald-700 inline-flex items-center gap-1"><Check className="size-3.5" /> Code from {ref.referrer_first_name}. You get {ref.discount_pct}% off labour</span>
              : ref && !ref.valid ? <span className="text-red-600">We couldn't find that code</span> : null}
          </div>
        </div>
      </div>
    </section>
  );
};
